import React, { useEffect, useState } from "react";
import { getBaseUrl } from "../../../api/baseApi";
import {
    Card,
    Row,
    Col,
    Button,
    Table,
    Tag,
    Tabs,
    Space,
    Typography,
    Statistic,
    Modal,
    message,
    Form,
    Input,
    Select,
    Upload,
    Image,
} from "antd";
import {
    HomeOutlined,
    UserOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import ManagementEndpoint from "../../../api/management";
import RentEndpoints from "../../../api/rent";
import { RentingEndpoint } from "../../../api";

const { Title, Text } = Typography;
const { TabPane } = Tabs;


const BASE_URL = getBaseUrl();

const getImageUrl = (photo) => {
    if (!photo) return "";

    try {
        // Coba parse JSON array
        const parsed = JSON.parse(photo);
        if (Array.isArray(parsed) && parsed.length > 0) {
            return BASE_URL + parsed[0]; // gunakan gambar pertama
        }
    } catch (err) {
        // Jika bukan JSON → gunakan langsung
        return BASE_URL + photo;
    }

    return ""; // fallback
};


// Transform transaction data to rental request format
const transformRentalRequests = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return [];

    return transactions.map((transaction) => {
        // Find the parent (deposit) transaction
        const parentTransaction = transaction.parentTransaction;


        return {
            id: transaction.id,
            tenantName: transaction.createdByUser?.name || "Unknown Tenant",
            unit: transaction.property?.name || "Unknown Property",
            depositAmount: parseFloat(parentTransaction?.nominal || 0),
            rentAmount: parseFloat(transaction.nominal || 0),
            totalPayment:
                parseFloat(parentTransaction?.nominal || 0) + parseFloat(transaction.nominal || 0),
            status: transaction.status,
            transactionData: transaction, // Keep original data for callbacks
            propertyId: transaction.property_id,
            photo: transaction.photo || parentTransaction?.photo,
        };
    });
};



function ManagedProperty() {
    const [properties, setProperties] = useState([]);
    const [rentalRequests, setRentalRequests] = useState([]);
    const [utilityInvoices, setUtilityInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [photoModalVisible, setPhotoModalVisible] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [requestRent, setRequestRent] = useState([])
    const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
    const [selectedInvoiceProperty, setSelectedInvoiceProperty] = useState(null);
    const [invoiceForm] = Form.useForm();
    const [uploadList, setUploadList] = useState([]);

    const management = new ManagementEndpoint();
    const rentEp = new RentEndpoints();
    const rentingEp = new RentingEndpoint();

    const showInvoiceModal = (record) => {
        setSelectedInvoiceProperty(record);
        setUploadList([]);
        setInvoiceModalVisible(true);
    };

    const handleRemovePhoto = (fileToRemove) => {
        setUploadList(prev => prev.filter(f => f !== fileToRemove));
    };
    console.log();

    const handleWithdrawRequest = async (id, mes) => {
        try {
            const rentEndp = new RentingEndpoint();
            await rentEndp.deleteRequest(id);
            const rentData = await rentEndp.getRequest();
            setRequestRent(rentData?.received_request_invoice || []);
            if (mes === 'acc') {
                message.success("Request sewa disetujui");
            } else {
                message.success("Request sewa dibatalkan");
            }
        } catch (err) {
            console.error(err);
            message.error("Gagal membatalkan request sewa");
        }
    }
    const handleVerifyRequest = async (id, mes) => {
        try {
            const rentEndp = new RentingEndpoint();
            await rentEndp.verifyRequest(id);
            const rentData = await rentEndp.getRequest();
            setRequestRent(rentData?.received_request_invoice || []);
            if (mes === 'acc') {
                message.success("Request sewa disetujui");
            } else {
                message.success("Request sewa dibatalkan");
            }
        } catch (err) {
            console.error(err);
            message.error("Gagal membatalkan request sewa");
        }
    }

    const handleRefundRental = async (record) => {

        try {
            setLoading(true);
            // Refund using the rent transaction ID
            await rentEp.refundTransaction(record.id);

            message.success('Deposit refunded successfully!');
            // Refresh data after refund
            const resultRent = await rentEp.getRents();
            const transformedRentalRequests = transformRentalRequests(resultRent.managed_rent_request);
            setRentalRequests(transformedRentalRequests);
        } catch (error) {
            console.error('Error refunding rental:', error);
            message.error('Failed to refund rental. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchManagedProperties = async () => {
            try {
                setLoading(true);
                setError(null);

                const resultProperty = await management.getManagedProperty();
                const resultRent = await rentEp.getRents();
                const rentData = await rentingEp.getRequest();

                setRequestRent(rentData.received_request_invoice || [])

                const managedBills = await management.getUtilitiesInvoice();

                // Flatten utility invoices into single array
                const flattenedInvoices = [
                    ...(managedBills?.waterInvocie || []),
                    ...(managedBills?.electricityInvocie || []),
                ];
                setUtilityInvoices(flattenedInvoices);

                // Transform rental requests from transaction data
                const transformedRentalRequests = transformRentalRequests(
                    resultRent.managed_rent_request
                );

                // Simpan hasil fetch
                setProperties(resultProperty);
                setRentalRequests(transformedRentalRequests);
            } catch (err) {
                console.error("Failed to fetch managed properties:", err);
                setError("Failed to load properties");
            } finally {
                setLoading(false);
            }
        };

        fetchManagedProperties();
    }, []);


    const requestColumns = [
        {
            title: "Property",
            dataIndex: "property",
            key: "property",
            render: (property) => (
                <div>
                    <div style={{ fontWeight: "bold" }}>{property?.name || "N/A"}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                        {property?.city}, {property?.province}
                    </div>
                </div>
            ),
        },
        {
            title: "Requested By",
            dataIndex: "createdByUser",
            key: "createdByUser",
            render: (createdByUser) => createdByUser?.name || "N/A",
        },
        {
            title: "Request Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (transaction_date) => formatDate(transaction_date),
        },
        {
            title: "Rent Amount",
            key: "nominal",
            render: (_, data) => `${parseFloat(data.nominal || 0)} ${data.rent_type}`,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={status === 'Pending' ? 'orange' : 'green'}>
                    {status?.toUpperCase() || 'PENDING'}
                </Tag>
            ),
        },

        {
            title: "Action",
            key: "id",
            dataIndex: "id",

            render: (id) => (
                <Space>
                    <Button
                        danger
                        size="small"
                        onClick={() => handleWithdrawRequest(id)}
                    >
                        Reject
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => handleVerifyRequest(id, "acc")}
                    >
                        Verify
                    </Button>
                </Space>
            ),
        },
    ]
    // Rental requests columns
    const rentalColumns = [
        {
            title: "Tenant",
            dataIndex: "tenantName",
            key: "tenantName",
            render: (name, record) => (
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <UserOutlined />
                        <span style={{ fontWeight: "bold" }}>{name}</span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                        {record.transactionData?.createdByUser?.email}
                    </div>
                </div>
            ),
        },
        {
            title: "Unit",
            dataIndex: "unit",
            key: "unit",
            render: (unit, record) => (
                <div>
                    <div style={{ fontWeight: "bold" }}>{unit}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                        {record.transactionData?.property?.city}, {record.transactionData?.property?.province}
                    </div>
                </div>
            ),
        },
        {
            title: "Start Rent",
            key: "startRent",
            render: (_, record) => formatDate(record.transactionData?.transaction_date),
        },
        {
            title: "End Rent",
            key: "endRent",
            render: (_, record) => formatDate(record.transactionData?.expired_date),
        },
        {
            title: "Deposit",
            dataIndex: "depositAmount",
            key: "depositAmount",
            render: (amount) => `Rp ${amount?.toLocaleString() || 0}`,
        },
        {
            title: "Deposit Status",
            key: "depositStatus",
            render: (_, record) => {
                const depositStatus = record.transactionData?.parentTransaction?.status;
                const statusConfig = {
                    'Pending': { color: 'orange', text: 'Pending' },
                    'Verified': { color: 'green', text: 'Paid' },
                    'Cancelled and Refunded': { color: 'red', text: 'Refunded' },
                };
                const config = statusConfig[depositStatus] || statusConfig['Pending'];
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: "Rent Amount",
            dataIndex: "rentAmount",
            key: "rentAmount",
            render: (amount) => `Rp ${amount?.toLocaleString() || 0}`,
        },
        {
            title: "Total Payment",
            dataIndex: "totalPayment",
            key: "totalPayment",
            render: (amount) => (
                <Text strong style={{ color: "#52c41a" }}>
                    Rp {amount?.toLocaleString() || 0}
                </Text>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                const statusConfig = {
                    pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Not Validated' },
                    Verified: { color: 'blue', icon: <CheckCircleOutlined />, text: 'Paid' },
                    'Check In': { color: 'green', icon: <CheckCircleOutlined />, text: 'Check In' },
                    'Check Out': { color: 'green', icon: <CheckCircleOutlined />, text: 'Check Out' },
                    'Cancelled and Refunded': { color: 'purple', icon: <ClockCircleOutlined />, text: 'Deposit Refunded' }
                };
                const config = statusConfig[status] || statusConfig.pending;
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.text}
                    </Tag>
                );
            },
        },

        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                return (
                    <Space>
                        {record.transactionData.parentTransaction.status === 'Verified' && (
                            <Button
                                danger
                                size="small"
                                onClick={() => handleRefundRental(record.transactionData.parentTransaction)}
                            >
                                Refund Deposit
                            </Button>
                        )}
                        {record.status === 'Check In' && (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => showInvoiceModal(record)}
                                icon={<PlusOutlined />}
                            >
                                Add Invoice
                            </Button>
                        )}
                    </Space>
                )
            }
        }
    ];

    // Bills columns
    // Format date helper
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        return new Date(timestamp).toLocaleDateString("id-ID", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Format currency helper
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
        }).format(amount);
    };


    // Handle refund utility invoice
    const handleRefundInvoice = async (record) => {
        try {
            await rentEp.refundTransaction(record.id);
            message.success('Utility invoice refunded successfully!');

            // Refresh utility invoices
            const refreshedBills = await management.getUtilitiesInvoice();

            const flattenedRefreshedInvoices = [
                ...(refreshedBills?.waterInvocie || []),
                ...(refreshedBills?.electricityInvocie || [])
            ];
            setUtilityInvoices(flattenedRefreshedInvoices);
        } catch (error) {
            console.error('Error refunding invoice:', error);
            message.error('Failed to refund utility invoice');
        }
    };

    const utilityInvoiceColumns = [
        {
            title: "Property",
            dataIndex: "property",
            key: "property",
            render: (property) => (
                <div>
                    <div style={{ fontWeight: "bold" }}>{property?.name}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                        {property?.full_address}, {property?.city}
                    </div>
                </div>
            ),
        },
        {
            title: "Type",
            dataIndex: "transaction_type",
            key: "type",
            render: (type) => (
                <Tag color={type === "water" ? "blue" : "yellow"}>{type?.toUpperCase()}</Tag>
            ),
        },
        {
            title: "Amount",
            dataIndex: "nominal",
            key: "amount",
            render: (amount) => (
                <Text strong style={{ color: "#52c41a" }}>
                    {formatCurrency(amount)}
                </Text>
            ),
        },
        {
            title: "Date",
            dataIndex: "transaction_date",
            key: "date",
            render: (date) => formatDate(date),
        },
        {

            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {

                const statusConfig = {
                    'Pending': { color: 'orange', text: 'Pending' },
                    'Verified': { color: 'green', text: 'Verified' },
                    'Cancelled': { color: 'red', text: 'Cancelled' },
                    'Refunded': { color: 'blue', text: 'Refunded' },
                    'Cancelled and Refunded': { color: 'purple', text: 'Deposit Refunded' }
                };
                const config = statusConfig[status] || statusConfig['Pending'];
                return <Tag color={config.color}>{config.text}</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {(record.status === 'Pending' || record.status === 'Verified') && record.status !== 'Cancelled and Refunded' && (
                        <Button
                            danger
                            size="small"
                            onClick={() => handleRefundInvoice(record)}
                        >
                            Refund
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="ltn__myaccount-tab-content-inner">
            <Title level={3} style={{ marginBottom: "24px" }}>
                <HomeOutlined style={{ marginRight: "8px" }} />
                Property Management Dashboard
            </Title>

            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: "24px" }}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Properties"
                            value={properties.length}
                            prefix={<HomeOutlined />}
                            valueStyle={{ color: "#3f8600" }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Pending Requests"
                            value={Array.isArray(requestRent) ? requestRent.filter((r) => r.status === "Pending").length : 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: "#cf1322" }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Deposit Validated"
                            value={
                                rentalRequests.filter((r) => r.status === "deposit_validated")
                                    .length
                            }
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: "#1890ff" }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Unpaid Bills"
                            value={utilityInvoices.filter(b => b.status === 'Pending').length}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: "#fa8c16" }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main Tabs */}
            <Card>
                <Tabs defaultActiveKey="properties" size="large">
                    <TabPane
                        tab={
                            <span>
                                <HomeOutlined />
                                Properties ({properties.length})
                            </span>
                        }
                        key="properties"
                    >
                        <div style={{ marginBottom: "16px" }}>
                            <Title level={4}>Managed Properties</Title>
                            <Text type="secondary">View and manage all your rental properties</Text>
                        </div>

                        <Row gutter={[16, 16]}>
                            {properties.map((item) => {
                                const property = item.property;
                                const photoUrl = property.photo
                                    ? getImageUrl(property.photo)
                                    : null;

                                return (
                                    <Col xs={24} sm={12} lg={8} key={item.id}>
                                        <Card
                                            hoverable
                                            cover={
                                                photoUrl ? (
                                                    <img
                                                        alt={property.name}
                                                        src={photoUrl}
                                                        style={{
                                                            height: "200px",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            height: "200px",
                                                            backgroundColor: "#f0f0f0",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <HomeOutlined
                                                            style={{
                                                                fontSize: "48px",
                                                                color: "#d9d9d9",
                                                            }}
                                                        />
                                                    </div>
                                                )
                                            }
                                        >
                                            <Card.Meta
                                                title={property.name}
                                                description={
                                                    <div>
                                                        <div style={{ marginBottom: "8px" }}>
                                                            <div
                                                                style={{
                                                                    color: "#666",
                                                                    fontSize: "12px",
                                                                }}
                                                            >
                                                                {property.city}, {property.province}
                                                            </div>
                                                        </div>
                                                        <Space wrap>
                                                            <Tag color="blue">
                                                                {property.property_type}
                                                            </Tag>
                                                            <Tag color="green">
                                                                Rp{" "}
                                                                {parseFloat(
                                                                    property.rent_price
                                                                ).toLocaleString()}
                                                                /mo
                                                            </Tag>
                                                        </Space>
                                                        <div style={{ marginTop: "8px" }}>
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: "12px" }}
                                                            >
                                                                {property.surface_area}m² •{" "}
                                                                {property.building_area}m² building
                                                            </Text>
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        {properties.length === 0 && (
                            <div style={{ textAlign: "center", padding: "40px" }}>
                                <HomeOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                                <div style={{ marginTop: "16px" }}>
                                    <Text type="secondary">No properties found</Text>
                                </div>
                            </div>
                        )}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <UserOutlined />
                                Rental Requests({Array.isArray(requestRent) ? requestRent.filter(r => r.status === 'Pending').length : 0})
                            </span >
                        }
                        key="requests"
                    >
                        <div style={{ marginBottom: "16px" }}>
                            <Title level={4}>Rental Requests Management</Title>
                            <Text type="secondary">
                                Validate the request so tenant can pay the invoice
                            </Text>
                        </div>

                        <Table
                            columns={requestColumns}
                            dataSource={Array.isArray(requestRent) ? requestRent : []}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[1]} of ${total} requests`,
                            }}
                            scroll={{ x: 800 }}
                        />

                        {
                            (!Array.isArray(requestRent) || requestRent.length === 0) && (
                                <div style={{ textAlign: "center", padding: "40px" }}>
                                    <UserOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                                    <div style={{ marginTop: "16px" }}>
                                        <Text type="secondary">No rental requests found</Text>
                                    </div>
                                </div>
                            )
                        }
                    </TabPane >
                    <TabPane
                        tab={
                            <span>
                                <UserOutlined />
                                Rented Property({rentalRequests.length})
                            </span >
                        }
                        key="rented"
                    >
                        <div style={{ marginBottom: "16px" }}>
                            <Title level={4}>Rental Property Management</Title>
                            <Text type="secondary">
                                Manage tenant rental requests with deposit validation workflow
                            </Text>
                        </div>

                        <Table
                            columns={rentalColumns}
                            dataSource={rentalRequests}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[1]} of ${total} requests`,
                            }}
                            scroll={{ x: 800 }}
                        />

                        {
                            rentalRequests.length === 0 && (
                                <div style={{ textAlign: "center", padding: "40px" }}>
                                    <UserOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                                    <div style={{ marginTop: "16px" }}>
                                        <Text type="secondary">No rental requests found</Text>
                                    </div>
                                </div>
                            )
                        }
                    </TabPane >

                    <TabPane
                        tab={
                            <span>
                                <DollarOutlined />
                                Bills & Utilities ({utilityInvoices.filter(r => r.status === 'Pending').length})
                            </span>
                        }
                        key="bills"
                    >
                        <div style={{ marginBottom: "16px" }}>
                            <Title level={4}>Bills & Utility Payments</Title>
                            <Text type="secondary">
                                Track and manage utility bills and payments
                            </Text>
                        </div>

                        <Table
                            columns={utilityInvoiceColumns}
                            dataSource={utilityInvoices}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} invoices`,
                            }}
                            scroll={{ x: 800 }}
                        />

                        {utilityInvoices.length === 0 && (
                            <div style={{ textAlign: "center", padding: "40px" }}>
                                <DollarOutlined style={{ fontSize: "48px", color: "#d9d9d9" }} />
                                <div style={{ marginTop: "16px" }}>
                                    <Text type="secondary">No utility invoices found</Text>
                                </div>
                            </div>
                        )}
                    </TabPane>
                </Tabs >
            </Card >

            {/* Photo Modal */}
            < Modal
                title="Payment Proof"
                open={photoModalVisible}
                onCancel={() => setPhotoModalVisible(false)}
                footer={null}
                width={600}
                centered
            >
                {selectedPhoto && (
                    <div style={{ textAlign: "center" }}>
                        <img
                            src={selectedPhoto}
                            alt="Payment Proof"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "400px",
                                objectFit: "contain",
                            }}
                        />
                    </div>
                )}
            </Modal >

            {/* Add Invoice Modal */}
            <Modal
                title={`Add Utility Invoice: ${selectedInvoiceProperty?.unit}`}
                open={invoiceModalVisible}
                onCancel={() => {
                    setInvoiceModalVisible(false);
                    invoiceForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <div>
                    {/* Property Information */}
                    {selectedInvoiceProperty && (
                        <div
                            style={{
                                marginBottom: 20,
                                padding: 16,
                                backgroundColor: "#f5f5f5",
                                borderRadius: 8,
                            }}
                        >
                            <h5>Property: {selectedInvoiceProperty.unit}</h5>
                            <p style={{ margin: 0, color: "#666" }}>
                                Tenant: {selectedInvoiceProperty.tenantName}
                            </p>
                        </div>
                    )}

                    {/* Add Utility Invoice Form */}
                    <Form
                        form={invoiceForm}
                        layout="vertical"
                        onFinish={async (values) => {
                            try {
                                const formData = new FormData();
                                console.log(selectedInvoiceProperty.transactionData.createdByUser.id);


                                // Add form data
                                formData.append("property_id", selectedInvoiceProperty.propertyId);
                                formData.append("transaction_type", values.type);
                                formData.append("teenantId", selectedInvoiceProperty.transactionData.createdByUser.id)
                                formData.append("nominal", values.amount);
                                formData.append("description", values.description || "");

                                // Add multiple photos

                                if (uploadList && uploadList.length > 0) {
                                    uploadList.forEach((file, index) => {
                                        formData.append(`photos[]`, file);
                                    });
                                }

                                // Submit the form data
                                await rentingEp.addUtilityInvoice(
                                    formData,
                                    values.type
                                );

                                message.success("Utility invoice added successfully!");

                                // Reset form and close modal
                                invoiceForm.resetFields();
                                setUploadList([]);
                                setInvoiceModalVisible(false);

                            } catch (error) {
                                console.error("Error adding utility invoice:", error);
                                message.error("Failed to add utility invoice");
                            }
                        }}
                    >
                        {/* Row 1: Type and Amount */}
                        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                            <Form.Item
                                name="type"
                                label="Utility Type"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select utility type",
                                    },
                                ]}
                                style={{ flex: 1 }}
                            >
                                <Select placeholder="Select type">
                                    <Select.Option value="water">Water</Select.Option>
                                    <Select.Option value="electricity">
                                        Electricity
                                    </Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="amount"
                                label="Amount"
                                rules={[
                                    { required: true, message: "Please enter amount" },
                                    {
                                        pattern: /^\d+(\.\d{1,2})?$/,
                                        message: "Please enter valid amount",
                                    },
                                ]}
                                style={{ flex: 1 }}
                            >
                                <Input
                                    prefix="Rp"
                                    placeholder="Enter amount"
                                    type="number"
                                />
                            </Form.Item>
                        </div>

                        {/* Row 2: Description */}
                        <div style={{ marginBottom: 16 }}>
                            <Form.Item
                                label="Description"
                                name="description"
                            >
                                <Input.TextArea
                                    placeholder="Enter description/notes"
                                    rows={3}
                                />
                            </Form.Item>
                        </div>

                        {/* Row 3: Upload Photos */}
                        <div style={{ marginBottom: 16 }}>
                            <Form.Item
                                label="Upload Photos"
                                name="photos"
                            >
                                {/* Hidden file input */}
                                <input
                                    type="file"
                                    name="photos"
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        const newList = [...uploadList, ...files];
                                        setUploadList(newList);

                                        // Update form data
                                        const fakeEvent = {
                                            target: {
                                                name: "photos",
                                                type: "file",
                                                files: files,
                                            },
                                        };
                                        // Note: handleInputChange would need to be adapted, but we'll handle it in form submission
                                    }}
                                    style={{ display: "none" }}
                                    id="photo-hidden-input"
                                />

                                {/* Ant Design Upload UI */}
                                <Upload
                                    listType="picture-card"
                                    multiple
                                    showUploadList={false}
                                    fileList={uploadList.map(f => ({
                                        uid: f.uid || f.name + Math.random(),
                                        name: f.name,
                                        status: 'done',
                                        originFileObj: f,
                                        url: URL.createObjectURL(f),
                                    }))}
                                    beforeUpload={() => false}
                                    onChange={({ fileList }) => {
                                        const newList = fileList.map(f => f.originFileObj).filter(Boolean);

                                        // Find new files
                                        const newFiles = newList.filter(f => !uploadList.includes(f));

                                        // Update uploadList
                                        setUploadList(newList);

                                        // Send only new files
                                        if (newFiles.length > 0) {
                                            const fakeEvent = {
                                                target: {
                                                    name: "photos",
                                                    type: "file",
                                                    files: newFiles,
                                                },
                                            };
                                            // Note: handleInputChange would need to be adapted, but we'll handle it in form submission
                                        }
                                    }}
                                >
                                    <div>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </div>
                                </Upload>

                                {/* PREVIEW + DELETE BUTTON */}
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    {uploadList?.map((file, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                position: "relative",
                                                width: 80,
                                                height: 80,
                                                borderRadius: 8,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Image
                                                src={URL.createObjectURL(file)}
                                                width={80}
                                                height={80}
                                                style={{ objectFit: "cover" }}
                                                preview={false}
                                            />

                                            {/* Delete Button */}
                                            <div
                                                onClick={() => handleRemovePhoto(file)}
                                                style={{
                                                    position: "absolute",
                                                    top: 2,
                                                    right: 2,
                                                    background: "rgba(0,0,0,0.6)",
                                                    color: "white",
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    cursor: "pointer",
                                                    fontSize: 12,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                ✕
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Form.Item>
                        </div>

                        {/* Row 3: Submit Button */}
                        <div style={{ textAlign: "right" }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                htmlType="submit"
                                size="large"
                            >
                                Add Invoice
                            </Button>
                        </div>
                    </Form>
                </div>
            </Modal>
        </div >
    );
}

export default ManagedProperty;
